import { CSSResultGroup, html } from 'lit'
import { classMap } from 'lit/directives/class-map.js'
import { customElement, property, query } from 'lit/decorators.js'
import { style } from './toggle.style.js'
import { BooleanFormElement } from 'elements/_shared/formElement.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'


@customElement('cse-toggle')
export class CSEToggle extends BooleanFormElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: String, attribute: 'id' })
  _id = ''

  @property({ type: String })
  name = ''

  @property({ type: String, reflect: true })
  value = ''

  @property({ type: Boolean })
  required = false

  @property({ type: Boolean })
  disabled = false

  @property({ type: Boolean, reflect: true })
  checked = false

  @query('.toggle')
  toggle!: HTMLDivElement

  private toggleClicked = () => {
    if (!this.disabled) {
      this.checked = !this.checked
      this.emit(this.checked)
    }
  }

  private toggleActivated = (event: KeyboardEvent) => {
    if (event.key === ' ' && !this.disabled) {
      this.checked = !this.checked
      this.emit(this.checked)
    }
  }

  private preventBrowserScroll = (event: KeyboardEvent) => {
    if (event.key === ' ') {
      event.preventDefault()
    }
  }

  private renderIconCircle = () => html`
    <span class="icon-circle">
      ${this.checked
        ? html`<cse-icon icon-role="cse-toggle.on" size="xxs"></cse-icon>`
        : html`<cse-icon icon-role="cse-toggle.off" size="xxs"></cse-icon>`}
    </span>
  `

  constructor() {
    super()
    this.addEventListener('click', this.toggleClicked)
  }

  override focus() {
    this.toggle.focus()
  }

  override render() {
    return html`
      <div
        class=${classMap({
          'cse-toggle': true,
          checked: this.checked,
          disabled: this.disabled,
        })}
      >
        <label for="${this._id}" class="label">
          <slot></slot>
        </label>
        <div
          class="toggle"
          tabindex="0"
          @keyup="${(e: KeyboardEvent) => this.toggleActivated(e)}"
          @keydown="${(e: KeyboardEvent) => this.preventBrowserScroll(e)}"
        >
          <input
            type="checkbox"
            id="${this._id}"
            name="${this.name}"
            value="${this.value}"
            ?required="${this.required}"
            ?disabled="${this.disabled}"
            ?checked="${this.checked}"
          />
          ${this.renderIconCircle()}
        </div>
      </div>
    `
  }
}

initializeIconsByRole({
  'cse-toggle.on': { name: 'check', style: 'regular' },
  'cse-toggle.off': { name: 'xmark', style: 'regular' },
})

declare global {
  interface HTMLElementTagNameMap {
    'cse-toggle': CSEToggle
  }
}
